// Globals
import { FC, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

// Models
import { IPromotion } from '@/models/promotion.model';

// Components
import { Modal, ModalSize, useForm } from '@/components';
import CouponForm from '../CouponForm/CouponForm';

// Helpers
import { getEditCouponInitialValues, TCreateCouponFormValues } from '../../helpers';
import { useUpdateCoupon } from '../../hooks';
import { UpdateCouponDTO } from '../../dtos';

type TEditCouponModalProps = {
  isOpen: boolean;
  onClose: () => void;
  coupon: IPromotion | null;
};

const EditCouponModal: FC<TEditCouponModalProps> = ({ isOpen, onClose, coupon }) => {
  const { t } = useTranslation();
  const [formInstance] = useForm();
  const { updateCoupon, isLoading } = useUpdateCoupon();

  const initialValues = useMemo(() => getEditCouponInitialValues(coupon), [coupon]);

  useEffect(() => {
    if (!coupon?.id) {
      return;
    }
    formInstance.setFieldsValue(getEditCouponInitialValues(coupon));
  }, [formInstance, coupon]);

  const handleUpdateCoupon = async (values: TCreateCouponFormValues) => {
    if (!coupon?.id) return;

    try {
      const dto = new UpdateCouponDTO({ ...values, couponId: coupon.id });
      await updateCoupon(dto);
      onClose();
    } catch (error) {
      console.error(error);
    }
  };

  return (
    <Modal destroyOnClose title={t('edit_coupon_title')} onCancel={onClose} visible={isOpen} size={ModalSize.HUGE}>
      <CouponForm
        formInstance={formInstance}
        submitText={t('update_btn')}
        onSubmit={handleUpdateCoupon}
        onReset={onClose}
        isLoading={isLoading}
        initialValues={initialValues}
      />
    </Modal>
  );
};

export default EditCouponModal;
